import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type DefectsListResponse,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { HelpTip } from '../components/HelpTip';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import { describeDetailReviewState, describeReasonCode } from './defectLabels';
import styles from './DefectsList.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface DefectsListProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/**
 * Project defects dashboard (Story #37/37b). Read-only list of the potential
 * product defects TestCraft recorded (a run that failed a storefront assertion),
 * worst-first. Honest by construction: every defect is shown as *potential* until
 * a person reviews it (the review workflow is #38 — there are no confirm/dismiss
 * controls here); an empty project is a healthy, non-error state. Each row drills
 * into its journey's execution evidence (#127). Manual Refresh, no polling.
 */
export function DefectsList({ client = defaultClient }: DefectsListProps) {
  const { currentOrg } = useOrg();
  const { id } = useParams<{ id: string }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [data, setData] = useState<DefectsListResponse | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !id) return;
    setPhase('loading');
    try {
      const next = await client.defectsList(orgId, id);
      setData(next);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || refreshing) return;
    setRefreshing(true);
    try {
      const next = await client.defectsList(orgId, id);
      setData(next);
      setStatusMessage('Defects updated.');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, refreshing]);

  const isEmpty = data !== null && data.defects.length === 0;

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <h1 id="defects-heading" className={styles.heading}>
          Defects
        </h1>
        <p className={styles.subheading}>
          Potential product defects TestCraft recorded on this project &mdash; runs that failed a
          storefront check. Each starts as potential until a person reviews it.{' '}
          <HelpTip term="potentialDefect" />
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading}>
            <Spinner />
            <span>Loading defects&hellip;</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && data ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
              <Link to={`/projects/${id ?? ''}/journey-health`} className={styles.backLink}>
                View journey health
              </Link>
            </div>

            {isEmpty ? (
              <div className={styles.panel}>
                <InlineAlert severity="success">
                  No product defects recorded. TestCraft hasn&rsquo;t recorded any potential product
                  defects for this project. New defects appear here automatically after a validation
                  run fails a storefront check.
                </InlineAlert>
              </div>
            ) : (
              <>
                <p className={styles.summary} role="status">
                  <span>Defects: {data.total}</span>
                  <span aria-hidden="true">·</span>
                  <span>Critical {data.rollup.critical}</span>
                  <span>High {data.rollup.high}</span>
                  <span>Medium {data.rollup.medium}</span>
                  <span>Low {data.rollup.low}</span>
                </p>

                <table className={styles.table} aria-labelledby="defects-heading">
                  <thead>
                    <tr>
                      <th scope="col">Severity</th>
                      <th scope="col">Journey</th>
                      <th scope="col">Failing step</th>
                      <th scope="col">Occurrences</th>
                      <th scope="col">First seen</th>
                      <th scope="col">Last seen</th>
                      <th scope="col">State</th>
                      <th scope="col">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.defects.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <StatusBadge kind="severity" status={d.severity} />
                        </td>
                        <td>
                          <span>{journeyTypeLabel(d.journeyType)}</span>
                          <span className={styles.secondary}>{d.journeyTitle}</span>
                        </td>
                        <td>
                          <span>Step {d.stepOrder}</span>
                          <span className={styles.secondary}>{describeReasonCode(d.reasonCode)}</span>
                        </td>
                        <td>{d.occurrenceCount}</td>
                        <td>{formatTime(d.firstDetectedAt) ?? '—'}</td>
                        <td>{formatTime(d.lastDetectedAt) ?? '—'}</td>
                        <td className={styles.state}>
                          <Link
                            to={`/projects/${id ?? ''}/defects/${d.id}`}
                            aria-label={`Review defect for ${journeyTypeLabel(d.journeyType)}`}
                          >
                            {describeDetailReviewState(d.reviewState)}
                          </Link>
                        </td>
                        <td>
                          <Link
                            to={`/projects/${id ?? ''}/journey-health/${d.journeyType}/execution`}
                            aria-label={`View run evidence for ${journeyTypeLabel(d.journeyType)} defect`}
                          >
                            View run evidence
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}
